import { getSupabase } from "../supabase/client";

type Json = Record<string, any>;

function deepMerge<T extends Json>(base: T, patch: Json): T {
  const out: Json = Array.isArray(base)
    ? [...(base as any)]
    : { ...(base as any) };

  for (const [k, v] of Object.entries(patch)) {
    if (k === "facilities" && Array.isArray(v)) {
      if (v.length > 0) {
        out[k] = v;
      } else if (base[k] && Array.isArray(base[k])) {
        out[k] = base[k];
      }
    } else if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = deepMerge((base as any)?.[k] ?? {}, v);
    } else {
      out[k] = v;
    }
  }
  return out as T;
}

export async function patchProjectKpis(
  projectId: string | undefined,
  patch: Json,
  rootUpdates?: { name?: string; currency?: string; stage?: string },
): Promise<Json> {
  if (!projectId) {
    console.warn("[patchProjectKpis] No projectId provided, skipping save");
    return patch;
  }

  const sb = getSupabase();

  const { data: session } = await sb.auth.getSession();
  const uid = session?.session?.user?.id;
  if (!uid) throw new Error("No Supabase session. Please sign in again.");

  const { data: row, error: selErr } = await sb
    .from("projects")
    .select("id, owner_id, kpis")
    .eq("id", projectId)
    .single();

  if (selErr) throw new Error(`Could not load project: ${selErr.message}`);
  if (!row) throw new Error("Project not found.");
  if (row.owner_id && row.owner_id !== uid) {
    throw new Error(
      "You do not have permission to edit this project (owner mismatch).",
    );
  }

  const next = deepMerge(row.kpis ?? {}, patch);

  const updates: any = {
    kpis: next,
    updated_at: new Date().toISOString(),
  };

  if (rootUpdates) {
    if (rootUpdates.name !== undefined) updates.name = rootUpdates.name;
    if (rootUpdates.currency !== undefined)
      updates.currency = rootUpdates.currency;
    if (rootUpdates.stage !== undefined) updates.stage = rootUpdates.stage;
  }

  const { error: updErr } = await sb
    .from("projects")
    .update(updates)
    .eq("id", projectId);

  if (updErr) throw new Error(`Saving KPIs failed: ${updErr.message}`);

  return next;
}

export async function patchProject(
  projectId: string | undefined,
  updates: { name?: string; currency?: string; stage?: string },
): Promise<void> {
  if (!projectId) {
    console.warn("[patchProject] No projectId provided, skipping save");
    return;
  }

  const sb = getSupabase();

  const { data: session } = await sb.auth.getSession();
  const uid = session?.session?.user?.id;
  if (!uid) throw new Error("No Supabase session. Please sign in again.");

  const { data: row, error: selErr } = await sb
    .from("projects")
    .select("id, owner_id")
    .eq("id", projectId)
    .single();

  if (selErr) throw new Error(`Could not load project: ${selErr.message}`);
  if (!row) throw new Error("Project not found.");
  if (row.owner_id && row.owner_id !== uid) {
    throw new Error(
      "You do not have permission to edit this project (owner mismatch).",
    );
  }

  const { error: updErr } = await sb
    .from("projects")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", projectId);

  if (updErr) throw new Error(`Updating project failed: ${updErr.message}`);
}
